import { memo, useRef, useState, useMemo, type ChangeEvent, type DragEvent } from "react";
import { Button, Input, Stack, styled, Typography } from "~/common/mui";
import { BulkUploadSource, BulkUploadState, type Msg } from "~/features/bulkAddToCart/types/models";
import { useTranslation } from "react-i18next";
import { FileUploadArea } from "~/features/QuoteSplit/components/FileUploadArea";
import { URL_BULK_TEMPLATE_XLSX } from "~/utils/url/staticUrls";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { C_OUTLINE, C_SURFACE_CONTAINER_HIGH } from "~/common/components/icon/Colors";

interface FileUploadSectionProps {
  onFileSelected: (file: File) => void;
  onFileError?: () => void;
  state: BulkUploadState;
  source: BulkUploadSource;
  messages: Msg[];
}

const FileUploadSection = memo((props: FileUploadSectionProps) => {
  const { onFileSelected, state, source, messages } = props;
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fileFormatText = t("bulkUpload.uploadDialog.fileFormat");

  // Drag and Drop Handlers
  const dragenter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === "file") {
      setIsDragging(true);
    }
  };

  const dragover = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dragleave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFile = (file: File) => {
    setFileName(file.name);
    onFileSelected(file);
  };

  const drop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const selectFile = () => {
    inputRef.current?.click();
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    formRef.current?.reset();
  };

  const btnDisabled = state === BulkUploadState.UPLOADING;
  const isUploading = state === BulkUploadState.UPLOADING;
  const isSuccess = state === BulkUploadState.UPLOAD_SUCCESS;
  const disableInteractions = isUploading || state === BulkUploadState.LOADING_CART;
  const isInvalid = state === BulkUploadState.ERROR && source === BulkUploadSource.FILE;
  const hasErrorMessage = isInvalid && messages.length !== 0;

  const helperMsg = useMemo(() => {
    if (hasErrorMessage) {
      return messages.map((msg) => msg.label).join(", ");
    }
    return t("bulkUpload.file.helper");
  }, [hasErrorMessage, messages, t]);

  return (
    <>
      <Stack gap={1} spacing={1}>
        <FileDropArea
          onDragEnter={disableInteractions ? undefined : dragenter}
          onDragLeave={disableInteractions ? undefined : dragleave}
          onDragOver={disableInteractions ? undefined : dragover}
          onDrop={disableInteractions ? undefined : drop}
        >
          <form style={{ display: "none" }} ref={formRef}>
            <Input type="file" inputRef={inputRef} onChange={onFileChange} hidden disabled={btnDisabled} />
          </form>

          <FileUploadArea
            selectFile={selectFile}
            disabled={btnDisabled}
            fileFormatText={fileFormatText}
            isDragging={isDragging}
          />
        </FileDropArea>

        {isSuccess && <Typography color="primary">{t("bulkUpload.file.successLabel")}</Typography>}
        {hasErrorMessage && <Typography color="error">{helperMsg}</Typography>}
      </Stack>

      <Button
        component="a"
        href={URL_BULK_TEMPLATE_XLSX}
        variant="text"
        startIcon={<FileDownloadOutlinedIcon />}
        download
        sx={{ marginTop: "8px" }}
      >
        {t("bulkUpload.download.template.text")}
      </Button>
    </>
  );
});

const FileDropArea = styled(Stack)({
  width: "100%",
  gap: "8px",
  "& .MuiPaper-root": {
    height: "172px",
    backgroundColor: C_SURFACE_CONTAINER_HIGH,
    border: `1px dashed ${C_OUTLINE}`,
  },
});

export default FileUploadSection;
